import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';
import { IconAI } from '../icons/index.js';

/**
 * Variantes de bouton MyTaki (brand book Taki).
 *
 * - `primary`  : CTA lime, encre foncee. Le lime est RARE : reserver aux
 *                actions principales, jamais decoratif.
 * - `secondary`: surface blanche bordee, pour les actions secondaires.
 * - `ghost`    : transparent, texte attenue. Trois places seulement : le
 *                renoncement a cote de l'action qu'il refuse, l'action repetee
 *                sur chaque ligne, l'outil dans un controle (regle G28 de
 *                docs/GABARIT-ECRAN.md). Ailleurs, `secondary`.
 * - `dark`     : encre pleine, texte blanc. Contraste fort sur fond clair.
 * - `danger`   : surface blanche, texte rouge. Actions destructrices.
 * - `ai`       : identique a `primary` avec le logo IconAI plein a gauche.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'dark' | 'danger' | 'ai';

/** Tailles de bouton. `md` (h-10) est la taille par defaut. */
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Action ponctuelle en cours (submit, sauvegarde) : spinner .taki-spinner a
   * la place du contenu, bouton desactive, aria-busy. La largeur ne bouge pas
   * (le libelle reste en place, rendu invisible) : aucun saut de mise en page.
   */
  loading?: boolean;
}

const baseClasses =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap select-none font-sans font-semibold ' +
  'border box-border cursor-pointer transition-all duration-150 ' +
  'focus-visible:outline-none ' +
  'disabled:cursor-not-allowed disabled:pointer-events-none';

const variantClasses: Record<ButtonVariant, string> = {
  // Survol SOBRE, choisi par le fondateur le 04/08/2026 sur la planche de 10
  // propositions : le lime s'eclaircit d'un cran, RIEN ne bouge, aucune ombre.
  // Le clic, lui, s'enfonce (fond presse + ombre interieure). L'ancien relief
  // (montee de 1px + halo --shadow-accent) est abandonne : il se faisait
  // trancher par tout conteneur defilant et bougeait la ligne de base.
  primary:
    'bg-accent border-accent-border text-accent-label ' +
    'hover:bg-accent-hover ' +
    'active:bg-accent-press active:shadow-press ' +
    'focus-visible:shadow-ring ' +
    'disabled:bg-control-disabled-bg disabled:border-border disabled:text-control-disabled-ink disabled:shadow-none',
  secondary:
    'bg-surface border-border text-ink-900 ' +
    'hover:bg-surface-1 hover:border-border-strong ' +
    'focus-visible:shadow-ring ' +
    'disabled:bg-surface-1 disabled:text-control-disabled-ink',
  ghost:
    'bg-transparent border-transparent text-ink-500 ' +
    'hover:bg-surface-3 hover:text-ink-900 ' +
    'focus-visible:shadow-ring ' +
    'disabled:text-control-disabled-ink',
  dark:
    'bg-ink-900 border-ink-900 text-white ' +
    'hover:bg-ink-700 hover:border-ink-700 ' +
    'active:bg-ink-900 ' +
    'focus-visible:shadow-ring ' +
    'disabled:bg-ink-400 disabled:border-ink-400',
  danger:
    'bg-surface border-danger-border text-danger ' +
    'hover:bg-danger-tint ' +
    'focus-visible:shadow-ring ' +
    'disabled:text-control-disabled-ink',
  // `ai` suit `primary` a l'identique, survol sobre compris.
  ai:
    'bg-accent border-accent-border text-accent-label ' +
    'hover:bg-accent-hover ' +
    'active:bg-accent-press active:shadow-press ' +
    'focus-visible:shadow-ring ' +
    'disabled:bg-control-disabled-bg disabled:border-border disabled:text-control-disabled-ink disabled:shadow-none',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 rounded-sm text-xs px-3.5',
  md: 'h-10 rounded-md text-sm px-[1.125rem]',
  lg: 'h-12 rounded-[0.75rem] text-[0.9375rem] px-6',
};

/** Cote (px) du logo Lia selon la taille du bouton. */
const aiIconSize: Record<ButtonSize, number> = {
  sm: 14,
  md: 16,
  lg: 18,
};

/**
 * Bouton primaire de l'interface MyTaki. Theme via les tokens (classes
 * Tailwind), forwardRef, type "button" par defaut.
 *
 * `whitespace-nowrap` est VOLONTAIRE : un libelle de bouton ne se coupe pas
 * tout seul sur deux lignes. La troncature est une decision de la ZONE qui
 * contraint la largeur (cf. la zone 6 de `ScreenLayout`, G13), jamais du
 * bouton : ici le libelle arrive en enfant libre sous un `display:contents`,
 * donc en boite anonyme, sur laquelle aucune classe ne se pose.
 *
 * @example
 * <Button variant="primary">Action principale</Button>
 * <Button variant="ai" size="md">Lia de Taki</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'md',
    type = 'button',
    loading = false,
    disabled,
    children,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        loading && 'relative',
        className,
      )}
      {...props}
    >
      {loading && (
        /* Spinner centre par-dessus le contenu rendu invisible : la largeur
           du bouton ne bouge pas (pas de saut). */
        <span aria-hidden className="absolute inset-0 flex items-center justify-center">
          <span className="taki-spinner" />
        </span>
      )}
      {/* display:contents : le contenu reste des items flex directs du bouton
          (gap conserve) ; invisible pendant le chargement, il continue de
          reserver exactement la meme largeur. */}
      <span className={cn('contents', loading && 'invisible')}>
        {variant === 'ai' ? (
          <IconAI size={aiIconSize[size]} className="shrink-0 text-current" />
        ) : null}
        {children}
      </span>
    </button>
  );
});
